import { useState } from 'react';
import { useLeadActivities, useAddLeadActivity } from '@/hooks/useLeadActivities';
import { useCallLogs } from '@/hooks/useCallTracking';
import { useLead } from '@/hooks/useLeads';
import { useUpdateLeadDetails } from '@/hooks/useLeadActions';
import { openRecording } from '@/utils/openRecording';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Mail,
  MessageSquare,
  FileText,
  Activity,
  Plus,
  CalendarClock,
  Clock,
  Voicemail,
  Play,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow, addDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import type { LeadActivity } from '@/types/leads';
import type { CallLogWithRelations } from '@/types/calls';
import { getDispositionBucket, DISPOSITION_BUCKET_LABELS, DISPOSITION_BUCKET_COLORS } from '@/types/calls';

interface LeadActivityTimelineProps {
  leadId: string;
}

// Unified timeline entry — either a manual activity or a call log
type TimelineEntry =
  | { kind: 'activity'; data: LeadActivity & { profiles?: { full_name: string } | null }; timestamp: Date }
  | { kind: 'call'; data: CallLogWithRelations; timestamp: Date };

const activityIcons: Record<LeadActivity['activity_type'], React.ReactNode> = {
  call: <Phone className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  sms: <MessageSquare className="h-4 w-4" />,
  note: <FileText className="h-4 w-4" />,
  status_change: <Activity className="h-4 w-4" />,
  other: <Plus className="h-4 w-4" />,
  lead_updated: <FileText className="h-4 w-4" />,
};

const QUICK_FOLLOW_UP_OPTIONS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'In 7 days', days: 7 },
];

function formatDuration(seconds?: number | null) {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function RecordingPlayButton({ call }: { call: CallLogWithRelations }) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handlePlay = async () => {
    if (call.recording_url) {
      window.open(call.recording_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setLoading(true);
    const err = await openRecording(call.id);
    if (err) toast({ title: 'Recording unavailable', description: err, variant: 'destructive' });
    setLoading(false);
  };

  return (
    <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={handlePlay} disabled={loading}>
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
      Recording
    </Button>
  );
}

function CallEntry({ call }: { call: CallLogWithRelations }) {
  const isOutbound = call.direction === 'outbound';
  const bucket = getDispositionBucket(call.status);
  const isVoicemail = bucket === 'voicemail';
  const isMissed = bucket === 'missed' || bucket === 'abandoned';
  const agentName = call.profiles?.full_name ?? call.agent_name;
  const talkDuration = formatDuration(call.talk_time_seconds);
  const totalDuration = formatDuration(call.duration_seconds);
  const duration = talkDuration || totalDuration;
  const timestamp = new Date(call.started_at || call.created_at || '');
  const hasRecording = call.recording_available || !!call.recording_url;
  const callerNumber = call.direction === 'inbound' ? call.caller_number : call.receiver_number;

  const Icon = isVoicemail ? Voicemail : isMissed ? PhoneMissed : isOutbound ? PhoneOutgoing : PhoneIncoming;
  const iconBg = isVoicemail
    ? 'bg-info/10 text-info'
    : isMissed
    ? 'bg-destructive/10 text-destructive'
    : isOutbound
    ? 'bg-info/10 text-info'
    : 'bg-success/10 text-success';

  const callLabel = isOutbound ? 'Outgoing Call' : 'Incoming Call';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-2 rounded-full shrink-0 ${iconBg}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm font-medium">{callLabel}</CardTitle>
                <Badge
                  variant="outline"
                  className={`text-xs px-1.5 py-0 h-5 ${DISPOSITION_BUCKET_COLORS[bucket]} border-current/30`}
                >
                  {DISPOSITION_BUCKET_LABELS[bucket]}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {agentName && <span>{agentName} · </span>}
                {formatDistanceToNow(timestamp, { addSuffix: true })}
              </p>
            </div>
          </div>
          {hasRecording && <RecordingPlayButton call={call} />}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
          {callerNumber && (
            <span className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {callerNumber}
            </span>
          )}
          {duration && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {duration}
            </span>
          )}
          {call.disposition && (
            <span className="capitalize">{call.disposition.replace(/_/g, ' ')}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function LeadActivityTimeline({ leadId }: LeadActivityTimelineProps) {
  const { data: activities = [], isLoading: activitiesLoading } = useLeadActivities(leadId);
  const { data: callLogs = [], isLoading: callsLoading } = useCallLogs({ leadId, limit: 200 });
  const { data: lead } = useLead(leadId);
  const addActivity = useAddLeadActivity();
  const updateLead = useUpdateLeadDetails();
  const [isAdding, setIsAdding] = useState(false);
  const [activityType, setActivityType] = useState<LeadActivity['activity_type']>('note');
  const [description, setDescription] = useState('');
  const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
  const [followUpDays, setFollowUpDays] = useState<string>('1');

  const isLoading = activitiesLoading || callsLoading;

  // Build merged, sorted timeline
  const timeline: TimelineEntry[] = [
    ...activities.map((a): TimelineEntry => ({
      kind: 'activity',
      data: a,
      timestamp: new Date(a.created_at),
    })),
    ...callLogs.map((c): TimelineEntry => ({
      kind: 'call',
      data: c,
      timestamp: new Date(c.started_at || c.created_at || ''),
    })),
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const handleAddActivity = () => {
    if (!description.trim()) return;

    const isContactActivity = ['call', 'email', 'sms'].includes(activityType);

    addActivity.mutate(
      {
        leadId,
        activityType,
        description: description.trim(),
      },
      {
        onSuccess: () => {
          if (isContactActivity) {
            const updates: Record<string, any> = {
              last_contact_date: new Date().toISOString(),
            };

            if (scheduleFollowUp && followUpDays) {
              const followUpDate = addDays(new Date(), parseInt(followUpDays));
              updates.next_follow_up_date = followUpDate.toISOString();

              if (lead?.status && !['converted', 'lost'].includes(lead.status)) {
                updates.status = 'needs_follow_up';
              }
            }

            updateLead.mutate({ leadId, updates });
          } else if (scheduleFollowUp && followUpDays) {
            const followUpDate = addDays(new Date(), parseInt(followUpDays));
            const updates: Record<string, any> = {
              next_follow_up_date: followUpDate.toISOString(),
            };
            if (lead?.status && !['converted', 'lost'].includes(lead.status)) {
              updates.status = 'needs_follow_up';
            }
            updateLead.mutate({ leadId, updates });
          }

          setDescription('');
          setIsAdding(false);
          setScheduleFollowUp(false);
          setFollowUpDays('1');
        },
      }
    );
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading activity...</div>;
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" />
          Activity
        </h3>
        {!isAdding && (
          <Button size="sm" variant="outline" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Activity
          </Button>
        )}
      </div>

      {isAdding && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label>Activity Type</Label>
              <Select value={activityType} onValueChange={(v: any) => setActivityType(v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter activity details..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Schedule Follow-up Section */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Schedule Follow-up</Label>
                </div>
                <Switch
                  checked={scheduleFollowUp}
                  onCheckedChange={setScheduleFollowUp}
                />
              </div>

              {scheduleFollowUp && (
                <div className="flex gap-2 pt-2">
                  {QUICK_FOLLOW_UP_OPTIONS.map((option) => (
                    <Button
                      key={option.days}
                      type="button"
                      size="sm"
                      variant={followUpDays === String(option.days) ? 'default' : 'outline'}
                      onClick={() => setFollowUpDays(String(option.days))}
                      className="flex-1"
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAdding(false);
                  setDescription('');
                  setScheduleFollowUp(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAddActivity}
                disabled={!description.trim() || addActivity.isPending}
              >
                {addActivity.isPending ? 'Adding...' : 'Add Activity'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No activities yet. Add one to get started!
          </p>
        ) : (
          timeline.map((entry) => {
            if (entry.kind === 'call') {
              return <CallEntry key={`call-${entry.data.id}`} call={entry.data} />;
            }

            const activity = entry.data;
            return (
              <Card key={`act-${activity.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        {activityIcons[activity.activity_type]}
                      </div>
                      <div>
                        <CardTitle className="text-sm font-medium capitalize">
                          {activity.activity_type.replace('_', ' ')}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {activity.profiles?.full_name ?? 'System'} •{' '}
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{activity.description}</p>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
